import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../context/AuthContext';

export default function Settings() {
  const { user, updateUser } = useAuth();
  const [username, setUsername] = useState('');
  const [loading, setLoading]   = useState(false);
  const [msg, setMsg]           = useState('');
  const [err, setErr]           = useState('');

  // Risk limits
  const [limits, setLimits] = useState({ maxDailyLoss: '', maxDrawdown: '' });
  const [initialLimits, setInitialLimits] = useState({ maxDailyLoss: '', maxDrawdown: '' });
  const [savingLimits, setSavingLimits] = useState(false);
  const [limitsMsg, setLimitsMsg] = useState('');
  const [limitsErr, setLimitsErr] = useState('');

  useEffect(() => {
    setUsername(user?.username || '');
  }, [user?.username]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/auth/me');
        if (cancelled) return;
        const next = {
          maxDailyLoss: r.data?.maxDailyLoss ?? '',
          maxDrawdown:  r.data?.maxDrawdown  ?? '',
        };
        setLimits(next);
        setInitialLimits(next);
      } catch (_) { /* ignore — defaults shown blank */ }
    })();
    return () => { cancelled = true; };
  }, []);

  function sanitize(v) {
    return v.toLowerCase().replace(/[^a-z0-9_.-]/g, '');
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setErr(''); setMsg('');
    if (!/^[a-z0-9_.-]{3,24}$/.test(username)) {
      setErr('Username must be 3–24 chars: lowercase letters, numbers, _ . -');
      return;
    }
    if (username === user?.username) {
      setMsg('No change.'); return;
    }
    setLoading(true);
    try {
      const r = await api.put('/auth/me', { username });
      updateUser({ username: r.data?.user?.username || username });
      setMsg('Username updated.');
    } catch (e2) {
      setErr(e2.response?.data?.error || 'Update failed');
    } finally {
      setLoading(false);
    }
  }

  const limitsDirty =
    String(limits.maxDailyLoss) !== String(initialLimits.maxDailyLoss) ||
    String(limits.maxDrawdown)  !== String(initialLimits.maxDrawdown);

  async function handleLimitsSubmit(e) {
    e.preventDefault();
    setLimitsErr(''); setLimitsMsg('');
    const payload = {
      maxDailyLoss: Number(limits.maxDailyLoss),
      maxDrawdown:  Number(limits.maxDrawdown),
    };
    if (!(payload.maxDailyLoss >= 0 && payload.maxDailyLoss <= 100)) {
      setLimitsErr('Max daily loss must be between 0 and 100 %.'); return;
    }
    if (!(payload.maxDrawdown >= 0 && payload.maxDrawdown <= 100)) {
      setLimitsErr('Max drawdown must be between 0 and 100 %.'); return;
    }
    setSavingLimits(true);
    try {
      await api.put('/auth/me', payload);
      setInitialLimits({
        maxDailyLoss: payload.maxDailyLoss,
        maxDrawdown:  payload.maxDrawdown,
      });
      setLimitsMsg('Risk limits updated.');
    } catch (e2) {
      setLimitsErr(e2.response?.data?.error || 'Update failed');
    } finally {
      setSavingLimits(false);
    }
  }

  return (
    <div className="row">
      <div className="col-lg-7 col-md-9 col-12">
        <div className="card">
          <div className="card-header">
            <h5 className="card-title mb-0">Account Settings</h5>
          </div>
          <div className="card-body">
            {!user?.username && (
              <div className="alert alert-info" style={{ fontSize: 13 }}>
                You don't have a username yet. Pick one — other users will see this
                instead of your email.
              </div>
            )}
            {msg && <div className="alert alert-success">{msg}</div>}
            {err && <div className="alert alert-danger">{err}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label className="text-white-50">Email</label>
                <input className="form-control" value={user?.email || ''} disabled />
                <small className="text-white-50">
                  Email is used to sign in and cannot be changed here.
                </small>
              </div>

              <div className="form-group">
                <label className="text-white-50">Username</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="e.g. og_trader"
                  autoCapitalize="off"
                  autoCorrect="off"
                  spellCheck="false"
                  value={username}
                  onChange={e => setUsername(sanitize(e.target.value))}
                />
                <small className="text-white-50">
                  3–24 chars · lowercase letters, numbers, <code>_ . -</code>
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={loading || username === (user?.username || '')}
              >
                {loading ? 'Saving...' : 'Save username'}
              </button>
            </form>
          </div>
        </div>

        <div className="card mt-3">
          <div className="card-header">
            <h5 className="card-title mb-0">Default Risk Limits</h5>
            <small className="text-white-50">
              Limits are enforced per MT5 account — equity, balance and open positions
              all belong to one broker account. These values seed a{' '}
              <strong>newly connected</strong> account; change an existing one from{' '}
              <Link to={`/${user?.role || 'copier'}/accounts`}>MT5 Accounts</Link>.
            </small>
          </div>
          <div className="card-body">
            {limitsMsg && <div className="alert alert-success">{limitsMsg}</div>}
            {limitsErr && <div className="alert alert-danger">{limitsErr}</div>}

            <form onSubmit={handleLimitsSubmit}>
              <div className="form-group">
                <label className="text-white-50">Max daily loss (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="form-control"
                  value={limits.maxDailyLoss}
                  onChange={e => setLimits(l => ({ ...l, maxDailyLoss: e.target.value }))}
                />
                <small className="text-white-50">
                  When an account's realised losses for the day reach this % of its balance,
                  every copy relationship on it is paused. The day's counter resets at
                  00:00 UTC, but copying stays paused until you resume it. Default 5%;
                  set to 0 to disable.
                </small>
              </div>

              <div className="form-group">
                <label className="text-white-50">Max account drawdown (%)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  step="0.1"
                  className="form-control"
                  value={limits.maxDrawdown}
                  onChange={e => setLimits(l => ({ ...l, maxDrawdown: e.target.value }))}
                />
                <small className="text-white-50">
                  Drawdown from an account's own equity peak. New copies on that account are
                  skipped while breached. Set to 0 to disable (default).
                </small>
              </div>

              <button
                type="submit"
                className="btn btn-primary"
                disabled={savingLimits || !limitsDirty}
              >
                {savingLimits ? 'Saving...' : 'Save risk limits'}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
